import { sum } from 'lodash';
import config from './funnel-chart-config.json';
import {
  ChartNodeType,
  ChartOptions,
  FunnelChartInfo,
  RawDataItem,
} from './funnel-chart-types';
import { createLink } from './link-utils';
import { createNodes } from './node-utils';

export const createChartInfo = (
  options: ChartOptions,
  rawData: RawDataItem[],
  width: number,
  height: number,
): FunnelChartInfo => {
  const { node } = options;
  const availableHeight = height;
  const startValueSum = sum(rawData.map(item => item.startValue));
  const endValueSum = sum(rawData.map(item => item.endValue));
  const smaller = Math.min(startValueSum, endValueSum);
  const bigger = Math.max(startValueSum, endValueSum);
  const relative = smaller / bigger;

  const startAvailableHeight =
    startValueSum > endValueSum ? availableHeight : availableHeight * relative;
  const endAvailableHeight =
    startValueSum < endValueSum ? availableHeight : availableHeight * relative;

  const startNodes = createNodes(
    rawData,
    width,
    availableHeight,
    startAvailableHeight,
    startValueSum,
    ChartNodeType.Start,
    node.minHeight,
  );

  const endNodes = createNodes(
    rawData,
    width,
    availableHeight,
    endAvailableHeight,
    endValueSum,
    ChartNodeType.End,
    node.minHeight,
  );

  const links = startNodes.map((startNode, index) =>
    createLink(startNode, endNodes[index], startNode.color),
  );

  const totals = {
    [ChartNodeType.Start]: startValueSum,
    [ChartNodeType.End]: endValueSum,
  };

  return {
    startNodes,
    endNodes,
    links,
    totals,
  };
};

export const createFunnelChartOptions = (): Readonly<ChartOptions> => {
  return Object.freeze({ ...config });
};

export const sumNodeValues = (
  rawData: RawDataItem[],
  nodeType: ChartNodeType,
) => {
  return sum(
    rawData.map(item =>
      nodeType === ChartNodeType.Start ? item.startValue : item.endValue,
    ),
  );
};
